/**
 * A littermate. **The same mesh as Rosalie, in a different dye.**
 *
 * That is the whole design and it is not laziness. They are three days old and
 * they are identical; a sibling built as its own slightly-different model would
 * be a *character*, and beat 1 does not want a character. It wants a shape
 * exactly like the player's, lying in the straw beside her, so that when one of
 * them is gone the player recognises the gap as being her own shape missing.
 *
 * The only difference is a paler, cooler dye (`SIBLING` in `palette.ts`), which
 * exists purely so the player can tell which pig they are driving. It is the
 * smallest difference that answers that question, and it should stay the smallest.
 *
 * ## Why there is almost no rig here
 *
 * They are asleep, not props. A slow breath is the smallest possible proof of
 * that, and when one disappears it makes the gap an absence rather than a
 * missing piece of scenery. One hind-leg twitch earns the same distinction
 * without turning either of them into a character or a miniature walk cycle.
 *
 * `sleepingPiglet()` still authors the sleeping pose once. `bornBay.ts` keeps
 * only its `piglet-lie` pivot and one hind leg live after merging; the player's
 * `RosalieRig` and clip set remain exclusively hers.
 */

import * as THREE from 'three';
import { SIBLING } from '@/render/palette';
import { ROSALIE_SCALE } from '@/world/testroom';
import { buildPlush, findRosalieParts } from './rosalie';
import type { AssetDefinition } from './types';

/**
 * A littermate asleep on her side.
 *
 * `seed` picks between a few authored variations so a row of them does not read
 * as a photocopy. It is an index and not a random source: the world places these
 * at fixed positions and a pose that changed between two runs would make every
 * screenshot in `docs/verification/` unreproducible.
 *
 * The variations are small on purpose. A litter asleep against a heat lamp really
 * does all lie the same way — they pile toward the warmth — and staggering them
 * for visual interest would lose that.
 */
export function sleepingPiglet(seed = 0): THREE.Group {
  const group = new THREE.Group();
  group.name = 'piglet';

  const body = buildPlush(SIBLING, 'piglet-body');
  const parts = findRosalieParts(body);

  // Onto her left side, head tipped back into the pile. Rotating the whole body
  // rather than posing the legs individually, for the same reason `sow.ts` builds
  // standing and lays over: the pose is one transform, and the mesh underneath
  // stays the mesh everything else was authored against.
  const lie = new THREE.Object3D();
  lie.name = 'piglet-lie';
  lie.rotation.z = Math.PI / 2;
  lie.add(body);
  group.add(lie);

  // Four authored variations, cycled. Head angle and how tightly the legs are
  // drawn in — a sleeping piglet curls, and how far it has curled is the only
  // thing that visibly differs between two of them at this size.
  const variants = [
    { head: 0.22, curl: 0.5, roll: 0.0 },
    { head: -0.14, curl: 0.78, roll: 0.09 },
    { head: 0.34, curl: 0.36, roll: -0.07 },
    { head: 0.05, curl: 0.64, roll: 0.14 },
  ] as const;
  const v = variants[((seed % variants.length) + variants.length) % variants.length] ?? variants[0];

  parts.head.rotation.x = v.head;
  lie.rotation.x = v.roll;
  // Front pair forward, back pair drawn under — a curl, not a sprawl. The signs
  // differ because the two pairs fold toward each other.
  parts.legFL.rotation.x = -v.curl;
  parts.legFR.rotation.x = -v.curl * 0.82;
  parts.legBL.rotation.x = v.curl;
  parts.legBR.rotation.x = v.curl * 0.86;
  // Ears fall the way gravity takes them once she is on her side.
  parts.earL.rotation.z += 0.24;
  parts.earR.rotation.z += 0.24;
  parts.tail.rotation.x = -0.2;

  // Settle onto y = 0, measured. Same reasoning as `sow.ts`: the resting height of
  // a body rotated onto its side with four folded limbs is arithmetic that is
  // wrong once and then silently wrong for ever, and `asset.spec.ts` holds the
  // contact plane to a millimetre.
  //
  // Written onto `lie.position`, which is in the unrotated group's frame and
  // therefore world-aligned — not onto the body inside the rotation, where "up"
  // is a different axis and every sign is a coin flip.
  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  lie.position.y -= bounds.min.y;
  lie.position.x -= (bounds.min.x + bounds.max.x) / 2;
  lie.position.z -= (bounds.min.z + bounds.max.z) / 2;

  return group;
}

export const pigletAsset: AssetDefinition = {
  id: 'piglet',
  label: 'Piglet (littermate)',
  description:
    "A littermate asleep on her side. Rosalie's mesh in a paler dye — identical on purpose, so the empty straw reads as her own shape missing.",
  // Her standing extents with two axes swapped, as `sow.ts` does: what she stands
  // tall is now across, and what she measures across is now her height.
  footprint: {
    width: ROSALIE_SCALE.height,
    height: ROSALIE_SCALE.width,
    length: ROSALIE_SCALE.length,
  },
  create: () => sleepingPiglet(0),
};
